import * as React from 'react';
import { useDebounceCallback } from '../../../../hooks/useDebounceValue';
import type { ComposerReplyType } from './types';

const COMPOSER_DRAFT_VERSION = 1;
const COMPOSER_DRAFT_PERSIST_DELAY_MS = 500;

export interface StoredComposerDraft {
	version: typeof COMPOSER_DRAFT_VERSION;
	html: string;
	replyType: ComposerReplyType;
	ccValue: string;
	bccValue: string;
	forwardToValue: string;
	forwardThreadId?: string;
	updatedAt: string;
}

/** Per-agent, per-ticket local draft storage so navigating away never drops an in-progress reply. */
export const getComposerDraftStorageKey = (userId: string, ticketId: string): string =>
	`levitate:support-ticket-composer-draft:${userId}:${ticketId}`;

const isComposerReplyType = (value: unknown): value is ComposerReplyType =>
	value === 'reply' || value === 'note' || value === 'forward';

const hasForwardRecipientDraftData = ({
	bccValue,
	ccValue,
	forwardThreadId,
	forwardToValue,
	replyType,
}: Pick<StoredComposerDraft, 'bccValue' | 'ccValue' | 'forwardThreadId' | 'forwardToValue' | 'replyType'>): boolean =>
	replyType === 'forward' &&
	!!forwardThreadId &&
	[forwardToValue, ccValue, bccValue].some(value => value.trim().length > 0);

const hasStoredComposerDraftData = (
	draft: Pick<StoredComposerDraft, 'html' | 'bccValue' | 'ccValue' | 'forwardThreadId' | 'forwardToValue' | 'replyType'>
): boolean => draft.html.trim().length > 0 || hasForwardRecipientDraftData(draft);

const getStoredForwardThreadId = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const normalizeStoredComposerDraft = (draft: StoredComposerDraft): StoredComposerDraft => {
	if (draft.replyType !== 'forward' || draft.forwardThreadId) return draft;
	return {
		...draft,
		replyType: 'note',
		ccValue: '',
		bccValue: '',
		forwardToValue: '',
		forwardThreadId: undefined,
	};
};

export const readStoredComposerDraft = (storageKey: string): StoredComposerDraft | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<StoredComposerDraft>;
		if (parsed.version !== COMPOSER_DRAFT_VERSION || typeof parsed.html !== 'string') {
			return null;
		}
		const storedDraft: StoredComposerDraft = {
			version: COMPOSER_DRAFT_VERSION,
			html: parsed.html,
			replyType: isComposerReplyType(parsed.replyType) ? parsed.replyType : 'reply',
			ccValue: typeof parsed.ccValue === 'string' ? parsed.ccValue : '',
			bccValue: typeof parsed.bccValue === 'string' ? parsed.bccValue : '',
			forwardToValue: typeof parsed.forwardToValue === 'string' ? parsed.forwardToValue : '',
			forwardThreadId: getStoredForwardThreadId(parsed.forwardThreadId),
			updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : '',
		};
		const normalizedDraft = normalizeStoredComposerDraft(storedDraft);
		if (!hasStoredComposerDraftData(normalizedDraft)) return null;
		return normalizedDraft;
	} catch {
		return null;
	}
};

const writeStoredComposerDraft = (storageKey: string, draft: StoredComposerDraft) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(storageKey, JSON.stringify(draft));
	} catch {
		// Storage can be unavailable or full; keep the in-memory editor usable either way.
	}
};

const removeStoredComposerDraft = (storageKey: string) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.removeItem(storageKey);
	} catch {
		// Ignore storage failures; successful sends still clear the in-memory editor.
	}
};

export const useComposerDraft = (storageKey: string) => {
	const initialDraftRef = React.useRef<StoredComposerDraft | null>(readStoredComposerDraft(storageKey));

	const clearDraft = React.useCallback(() => {
		removeStoredComposerDraft(storageKey);
	}, [storageKey]);

	return {
		initialDraft: initialDraftRef.current,
		clearDraft,
	};
};

interface UsePersistComposerDraftOptions {
	bccValue: string;
	ccValue: string;
	forwardToValue: string;
	forwardThreadId?: string;
	hasContent: boolean;
	/** The editor body as HTML — mention chips round-trip through their data attributes. */
	html: string;
	replyType: ComposerReplyType;
	storageKey: string;
}

// Placeholder blob URLs from in-flight image uploads die with the session; persisting them would
// restore broken images, so drafts keep only images that already have their hosted URL.
export const stripPlaceholderImages = (html: string): string => {
	if (!html.includes('src="blob:')) return html;
	const template = document.createElement('template');
	template.innerHTML = html;
	template.content.querySelectorAll('img[src^="blob:"]').forEach(image => image.remove());
	return template.innerHTML;
};

const buildStoredComposerDraft = ({
	bccValue,
	ccValue,
	forwardThreadId,
	forwardToValue,
	hasContent,
	html,
	replyType,
}: UsePersistComposerDraftOptions): StoredComposerDraft | null => {
	const draft: StoredComposerDraft = {
		version: COMPOSER_DRAFT_VERSION,
		html: hasContent ? stripPlaceholderImages(html) : '',
		replyType,
		ccValue,
		bccValue,
		forwardToValue,
		forwardThreadId: replyType === 'forward' ? forwardThreadId : undefined,
		updatedAt: new Date().toISOString(),
	};
	const normalizedDraft = normalizeStoredComposerDraft(draft);
	return hasStoredComposerDraftData(normalizedDraft) ? normalizedDraft : null;
};

const persistComposerDraft = (draftState: UsePersistComposerDraftOptions) => {
	const draft = buildStoredComposerDraft(draftState);
	if (!draft) {
		removeStoredComposerDraft(draftState.storageKey);
		return;
	}
	writeStoredComposerDraft(draftState.storageKey, draft);
};

export const usePersistComposerDraft = ({
	bccValue,
	ccValue,
	forwardThreadId,
	forwardToValue,
	hasContent,
	html,
	replyType,
	storageKey,
}: UsePersistComposerDraftOptions) => {
	const draftState = React.useMemo(
		() => ({
			bccValue,
			ccValue,
			forwardThreadId,
			forwardToValue,
			hasContent,
			html,
			replyType,
			storageKey,
		}),
		[bccValue, ccValue, forwardThreadId, forwardToValue, hasContent, html, replyType, storageKey]
	);
	const persistDraft = useDebounceCallback((nextDraftState: UsePersistComposerDraftOptions) => {
		persistComposerDraft(nextDraftState);
	}, COMPOSER_DRAFT_PERSIST_DELAY_MS);

	const draftStateRef = React.useRef(draftState);
	draftStateRef.current = draftState;

	React.useEffect(() => {
		persistDraft(draftState);
		return () => persistDraft.cancel();
	}, [draftState, persistDraft]);

	React.useEffect(() => {
		const flush = () => {
			persistDraft.cancel();
			persistComposerDraft(draftStateRef.current);
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === 'hidden') flush();
		};
		window.addEventListener('pagehide', flush);
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => {
			window.removeEventListener('pagehide', flush);
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [persistDraft]);
};
